import type { LisOrder, LisOrderStatus } from '@/api/lisApi'

/**
 * How each LIS order status is presented, and what the lab tech may do next.
 *
 * Pure and tested here rather than inline in the dialog, because the distinction
 * this encodes is the whole point of the screen and is easy to get subtly wrong:
 *
 * - **rejected** — Beaker answered and refused. Resending the same order number
 *   is wrong; the order must be corrected and placed as a NEW one. The backend
 *   409s a retry, so offering the button would be a dead end.
 * - **undelivered** — nobody acknowledged anything. The same order number may be
 *   used again, and the relay only resends orders in this state.
 *
 * Getting these two the wrong way round means either a lab tech who cannot
 * re-place a lost order, or one who quietly sends Beaker a duplicate.
 */
export type LisStatusTone = 'pending' | 'success' | 'error' | 'warning'

export interface LisStatusPresentation {
  label: string
  tone: LisStatusTone
  /** One line telling the user what this means for them. */
  summary: string
  /** Show a Retry that reuses the SAME order number. */
  canRetry: boolean
  /** Invite the user to correct the order and place a new one. */
  suggestNewOrder: boolean
}

const PRESENTATION: Record<LisOrderStatus, LisStatusPresentation> = {
  pending: {
    label: 'Sending',
    tone: 'pending',
    summary: 'Waiting for the laboratory to acknowledge the order.',
    canRetry: false,
    suggestNewOrder: false,
  },
  accepted: {
    label: 'Accepted',
    tone: 'success',
    summary: 'The laboratory has the order. Results arrive on this step when the test is done.',
    canRetry: false,
    suggestNewOrder: false,
  },
  rejected: {
    label: 'Rejected',
    tone: 'error',
    summary: 'The laboratory refused this order. Correct it and place a new order.',
    canRetry: false,
    suggestNewOrder: true,
  },
  undelivered: {
    label: 'Not delivered',
    tone: 'warning',
    summary: 'No acknowledgement came back. You can retry under the same order number.',
    canRetry: true,
    suggestNewOrder: false,
  },
}

export function presentLisStatus(status: LisOrderStatus): LisStatusPresentation {
  return PRESENTATION[status] ?? PRESENTATION.pending
}

/**
 * The reason line to show, if any. Beaker's `MSA-3` on a rejection, or why
 * nothing was delivered — always shown verbatim; it is the only thing that tells
 * the user what to fix.
 */
export function lisOrderReason(order: Pick<LisOrder, 'status' | 'reason'>): string | null {
  if (order.status === 'accepted' || order.status === 'pending') return null
  return order.reason?.trim() ? order.reason : null
}

/** `CTCC — CELL THERAPY CELL COUNT`, or just the code when there is no name. */
export function lisTestLabel(order: Pick<LisOrder, 'testCode' | 'testName'>): string {
  return order.testName ? `${order.testCode} — ${order.testName}` : order.testCode
}
